// src/hooks/useAbsences.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { formatLocalDate } from '@/lib/dateUtils';

// =====================================================
// QUERIES
// =====================================================

/**
 * Get absences for a specific child
 */
export const useChildAbsences = (childId, startDate, endDate, options = {}) => {
  return useQuery({
    queryKey: ['absences', childId, startDate, endDate],
    queryFn: async () => {
      let query = supabase
        .from('absences')
        .select('*')
        .eq('child_id', childId)
        .order('absence_date', { ascending: false });

      if (startDate) {
        query = query.gte('absence_date', startDate);
      }
      if (endDate) {
        query = query.lte('absence_date', endDate);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!childId,
    ...options
  });
};

/**
 * Get absences for a group on a specific date
 * Note: This queries by child's group, not a group_id on absences table
 */
export const useGroupAbsences = (groupId, date, options = {}) => {
  return useQuery({
    queryKey: ['absences', 'group', groupId, date],
    queryFn: async () => {
      // First get children in the group
      const { data: children, error: childrenError } = await supabase
        .from('children')
        .select('id')
        .eq('group_id', groupId);

      if (childrenError) throw childrenError;
      if (!children || children.length === 0) return [];

      const childIds = children.map(c => c.id);

      // Then get absences for those children on the date
      const { data, error } = await supabase
        .from('absences')
        .select('*, children(*)')
        .in('child_id', childIds)
        .eq('absence_date', date)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!groupId && !!date,
    ...options
  });
};

/**
 * Get all absences for parent's children
 */
export const useMyChildrenAbsences = (childIds = [], options = {}) => {
  return useQuery({
    queryKey: ['absences', 'my-children', childIds],
    queryFn: async () => {
      if (!childIds || childIds.length === 0) return [];

      const { data, error } = await supabase
        .from('absences')
        .select('*')
        .in('child_id', childIds)
        .order('absence_date', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data || [];
    },
    enabled: childIds && childIds.length > 0,
    ...options
  });
};

/**
 * Get upcoming absences for a child
 */
export const useUpcomingAbsences = (childId, options = {}) => {
  const today = formatLocalDate(new Date());

  return useQuery({
    queryKey: ['absences', 'upcoming', childId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('absences')
        .select('*')
        .eq('child_id', childId)
        .gte('absence_date', today)
        .order('absence_date', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!childId,
    ...options
  });
};

// =====================================================
// MUTATIONS
// =====================================================

/**
 * Report an absence
 * Accepts absenceData with child_id and optionally reporterName for notifications
 */
export const useReportAbsence = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reporterName, ...absenceData }) => {
      const { data, error } = await supabase
        .from('absences')
        .insert(absenceData)
        .select()
        .single();

      if (error) throw error;

      // Create notifications for teachers in the child's group
      try {
        // Get child with group info
        const { data: child } = await supabase
          .from('children')
          .select(`
            id,
            first_name,
            last_name,
            group_id,
            group:groups(id, name, main_teacher_id, teacher_ids)
          `)
          .eq('id', absenceData.child_id)
          .single();

        if (child?.group) {
          const teacherIds = new Set();
          if (child.group.main_teacher_id) {
            teacherIds.add(child.group.main_teacher_id);
          }
          child.group.teacher_ids?.forEach(tid => teacherIds.add(tid));

          if (teacherIds.size > 0) {
            const childName = `${child.first_name} ${child.last_name}`;
            const absenceDate = new Date(absenceData.absence_date).toLocaleDateString('pl-PL');
            const notifications = Array.from(teacherIds).map(teacherId => ({
              user_id: teacherId,
              type: 'absence_reported',
              title: 'Nieobecność zgłoszona',
              message: `${reporterName || 'Rodzic'} zgłosił nieobecność dla ${childName} (${absenceDate})`,
              priority: 'normal',
              read: false,
              metadata: {
                absence_id: data.id,
                child_id: child.id,
                child_name: childName,
                absence_date: absenceData.absence_date,
                group_id: child.group_id,
                group_name: child.group.name
              }
            }));

            await supabase.from('notifications').insert(notifications);
            logger.debug('Absence notifications created', { count: notifications.length });
          }
        }
      } catch (notifError) {
        // Non-critical - log but don't fail the absence report
        logger.error('Failed to create absence notifications:', notifError);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['absences'] });
      queryClient.invalidateQueries({ queryKey: ['daily_updates'] });
      toast.success('Nieobecność została zgłoszona');
    },
    onError: (error) => {
      logger.error('Report absence error', { message: error.message });
      toast.error(`Nie udało się zgłosić nieobecności: ${error.message}`);
    }
  });
};

/**
 * Update an absence
 */
export const useUpdateAbsence = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }) => {
      const { data, error } = await supabase
        .from('absences')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['absences'] });
      toast.success('Absence updated');
    },
    onError: (error) => {
      logger.error('Update absence error', { message: error.message });
      toast.error(`Failed to update: ${error.message}`);
    }
  });
};

/**
 * Cancel/delete an absence
 */
export const useCancelAbsence = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (absenceId) => {
      const { error } = await supabase
        .from('absences')
        .delete()
        .eq('id', absenceId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['absences'] });
      queryClient.invalidateQueries({ queryKey: ['daily_updates'] });
      toast.success('Nieobecność została anulowana');
    },
    onError: (error) => {
      logger.error('Cancel absence error', { message: error.message });
      toast.error('Nie udało się anulować nieobecności.');
    }
  });
};
